import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FocusScope } from '@radix-ui/react-focus-scope';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { ChevronRight, PanelLeftClose, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trDisplay } from '../../i18n';
import { Button } from '../ui/button';
import { TooltipPortalContainerContext } from '../ui/tooltip';
import './SWRConfigurationPanel.css';

const LEVELS = [
  { id: 'standard', label: 'Base', description: 'Capitale, prelievi e ipotesi essenziali.' },
  { id: 'intermediate', label: 'Intermedio', description: 'Aggiungi strategie, asset class e una riserva di liquidità.' },
  { id: 'advanced', label: 'Avanzato', description: 'Controlla anche costi, fiscalità e metodo di simulazione.' },
];

export function SWRConfigurationLevelSelector({ level, onLevelChange, compact = false }) {
  useTranslation();
  const id = useId();
  const selected = LEVELS.find(candidate => candidate.id === level) || LEVELS[0];
  return <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
    <span className={compact ? 'sr-only' : 'text-xs font-medium text-muted-foreground'} id={`${id}-label`}>{trDisplay('Livello di configurazione')}</span>
    <RadioGroup.Root orientation="horizontal" value={selected.id} onValueChange={onLevelChange} aria-labelledby={`${id}-label`}
      className="grid grid-cols-3 gap-1 rounded-md border border-border bg-muted p-1" data-testid="swr-configuration-levels">
      {LEVELS.map(option => <RadioGroup.Item type="button" key={option.id} value={option.id} data-testid={`swr-configuration-${option.id}`}
        className="min-h-11 min-w-0 rounded px-1 text-xs font-semibold text-muted-foreground transition-colors hover:bg-background active:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=checked]:bg-background data-[state=checked]:text-foreground sm:px-2 sm:text-sm">
        {trDisplay(option.label)}
      </RadioGroup.Item>)}
    </RadioGroup.Root>
    <p className="text-xs text-muted-foreground" data-testid="swr-configuration-level-description">{trDisplay(selected.description)}</p>
  </div>;
}

export default function SWRConfigurationPanel({ open, onOpenChange, level, onLevelChange, triggerId, children }) {
  useTranslation();
  const id = useId();
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const shellRef = useRef(null);
  const titleRef = useRef(null);
  const insidePointerEventsRef = useRef(new WeakSet());
  const wasOpenRef = useRef(false);
  const [headerBottom, setHeaderBottom] = useState(0);
  const [portalContainer, setPortalContainer] = useState(undefined);
  const setPanelElement = useCallback(node => {
    panelRef.current = node;
    setPortalContainer(node || undefined);
  }, []);
  const selected = LEVELS.find(candidate => candidate.id === level) || LEVELS[0];

  useLayoutEffect(() => {
    const header = document.querySelector('header');
    const measure = () => {
      const bounds = header?.getBoundingClientRect();
      setHeaderBottom(Math.max(0, bounds?.bottom || bounds?.height || 0));
    };
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (header) observer?.observe(header);
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const actions = panelRef.current?.querySelector('.swr-run-actions');
    if (!actions) return undefined;
    const measure = () => {
      shellRef.current?.style.setProperty('--swr-configuration-actions-height', `${Math.ceil(actions.getBoundingClientRect().height || 0)}px`);
    };
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(actions);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  useEffect(() => {
    if (open) titleRef.current?.focus({ preventScroll: true });
    else if (wasOpenRef.current) triggerRef.current?.focus({ preventScroll: true });
    wasOpenRef.current = open;
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    let background = triggerRef.current;
    while (background?.parentElement && background.parentElement !== document.body) background = background.parentElement;
    if (!background || background === shellRef.current) return undefined;
    const previous = background.getAttribute('inert');
    background.setAttribute('inert', '');
    return () => {
      if (previous === null) background.removeAttribute('inert');
      else background.setAttribute('inert', previous);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handlePointerDown = event => {
      const target = event.target;
      // React capture follows child portals too: asset selectors and help remain
      // part of configuration even though their DOM is attached to document.body.
      const inside = insidePointerEventsRef.current.has(event) || panelRef.current?.contains(target)
        || target.closest?.('[data-radix-popper-content-wrapper], [role="tooltip"]');
      insidePointerEventsRef.current.delete(event);
      if (!inside && event.button === 0) onOpenChange(false);
    };
    const handleKeyDown = event => {
      // Nested Radix menus consume Escape before this bubbling listener.
      if (event.key === 'Escape' && !event.defaultPrevented) {
        event.preventDefault();
        onOpenChange(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onOpenChange]);

  return <>
    <Button ref={triggerRef} id={triggerId} type="button" variant="outline"
      className="swr-configuration-trigger h-auto min-w-11 flex-col justify-between gap-3 rounded-none border-0 border-r bg-background px-0 py-3 text-foreground shadow-none active:bg-muted focus-visible:ring-2 focus-visible:ring-inset"
      style={{ top: `${headerBottom}px` }} hidden={open}
      onClick={() => onOpenChange(true)} aria-expanded={open} aria-controls={`${id}-panel`} aria-haspopup="dialog"
      aria-label={`${trDisplay('Modifica configurazione')} · ${trDisplay(selected.label)}`}
      data-testid="swr-configuration-open">
      <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="swr-configuration-trigger__label">
        <span className="font-semibold">{trDisplay('Modifica configurazione')}</span>
        <span className="text-xs text-muted-foreground"> · {trDisplay(selected.label)}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />
    </Button>
    {createPortal(<div ref={shellRef} className="swr-configuration-shell" hidden={!open} inert={!open ? true : undefined}
      aria-hidden={!open ? true : undefined} style={{ '--swr-configuration-top': `${headerBottom}px` }} data-testid="swr-configuration-shell">
      <div className="swr-configuration-scrim" aria-hidden="true" data-testid="swr-configuration-scrim"
        onClick={() => { if (open) onOpenChange(false); }} />
      <FocusScope asChild trapped={open} loop={open}
        onMountAutoFocus={event => event.preventDefault()} onUnmountAutoFocus={event => event.preventDefault()}>
        <section ref={setPanelElement} id={`${id}-panel`} role="dialog" aria-modal={open ? true : undefined}
          aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}
          className="swr-configuration-panel" data-testid="swr-configuration-panel"
          onPointerDownCapture={event => { insidePointerEventsRef.current.add(event.nativeEvent); }}>
          <TooltipPortalContainerContext.Provider value={portalContainer}>
          <div className="swr-configuration-panel__header">
            <div className="swr-configuration-panel__heading-row flex items-start justify-between gap-3">
              <div className="swr-configuration-panel__intro min-w-0">
                <h2 ref={titleRef} tabIndex={-1} id={`${id}-title`} className="text-base font-semibold text-foreground focus:outline-none">{trDisplay('Configura il tuo piano')}</h2>
                <p id={`${id}-description`} className="mt-0.5 text-xs text-muted-foreground">{trDisplay('Le modifiche restano in bozza. Esegui la simulazione per aggiornare i risultati.')}</p>
              </div>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="swr-configuration-panel__close h-11 shrink-0 gap-2 px-2 active:bg-muted focus-visible:ring-2"
                aria-label={trDisplay('Chiudi configurazione')} data-testid="swr-configuration-close">
                <PanelLeftClose className="h-5 w-5" aria-hidden="true" /><span className="hidden sm:inline">{trDisplay('Chiudi')}</span>
              </Button>
            </div>
            <div className="swr-configuration-panel__levels mt-1"><SWRConfigurationLevelSelector compact level={level} onLevelChange={onLevelChange} /></div>
          </div>
          <div className="swr-configuration-panel__body" data-testid="swr-configuration-body">{children}</div>
          </TooltipPortalContainerContext.Provider>
        </section>
      </FocusScope>
    </div>, document.body)}
  </>;
}
